import { useOnMountEffect } from "@mirage-web/utils/react-helpers";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Download } from "lucide-react";
import { Fragment, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { PermissionGuard } from "~/components/permission-guard";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import { ensureSession } from "~/modules/auth/credentials";
import { ApprovalSection } from "~/modules/human-editing/approve-request-button";
import { CompletionSection } from "~/modules/human-editing/completion-section";
import {
  getEditorPackageFileLabel,
  prepareEditorPackage,
} from "~/modules/human-editing/editor-package";
import { editorName } from "~/modules/human-editing/editors";
import { EmailDelivery } from "~/modules/human-editing/email-delivery";
import {
  type HumanEditingRequest,
  humanEditingEditorPackageQuery,
  humanEditingRequestQuery,
  humanEditingRequestsQuery,
} from "~/modules/human-editing/network";
import { SourceMedia } from "~/modules/human-editing/source-media";
import { formatRelativeTime } from "~/modules/videos/utils";

const statuses = {
  awaiting_approval: {
    label: "Awaiting Approval",
    color:
      "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200",
  },
  in_progress: {
    label: "In Progress",
    color:
      "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200",
  },
  complete: {
    label: "Complete",
    color:
      "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
  },
  rejected: {
    label: "Rejected",
    color:
      "border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200",
  },
};

export const Route = createFileRoute("/_dash/human-editing-requests")({
  validateSearch: z.object({ requestId: z.string().uuid().optional() }),
  beforeLoad: () => ensureSession(),
  component: () => (
    <PermissionGuard permission="projects:view">
      <RequestsPage />
    </PermissionGuard>
  ),
});

function RequestsPage() {
  const { requestId } = Route.useSearch();
  const [cursors, setCursors] = useState<string[]>([]);
  const query = humanEditingRequestsQuery.useQuery(
    { params: { cursor: cursors.at(-1), limit: 25 } },
    { refetchInterval: 15000 }
  );
  return (
    <div className="mx-auto max-w-7xl space-y-6 p-2 md:p-4">
      <h1 className="text-2xl font-semibold tracking-tight">Pro Editing</h1>
      {query.isError && (
        <p role="alert" className="text-destructive">
          Could not load requests. Retrying automatically…
        </p>
      )}
      {query.isPending && <p className="text-muted-foreground">Loading requests…</p>}
      {query.data && (
        <Card>
          <div className="@container overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-muted-foreground">
                <tr>
                  <th className="p-4 font-medium">User</th>
                  <th className="p-4 font-medium">Prompt</th>
                  <th className="p-4 font-medium">Status</th>
                  <th className="p-4 font-medium">Pro Editor</th>
                  <th className="p-4 font-medium">Approved At</th>
                  <th className="p-4 font-medium">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {query.data.requests.map((item) => {
                  const expanded = requestId === item.requestId;
                  return (
                    <Fragment key={item.requestId}>
                      <tr className="border-b align-top hover:bg-muted/30">
                        <td className="min-w-48 max-w-64 space-y-2 p-4">
                          <Link
                            to="/users/view/$userId"
                            params={{ userId: item.userId }}
                            className="font-medium hover:underline"
                          >
                            {item.userName || item.email}
                          </Link>
                          <div className="break-all text-xs text-muted-foreground">
                            {item.email}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            User:{" "}
                            <Link
                              to="/users/view/$userId"
                              params={{ userId: item.userId }}
                              className="break-all font-mono text-primary underline"
                            >
                              {item.userId}
                            </Link>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Org:{" "}
                            <Link
                              to="/organizations/view/$orgId"
                              params={{ orgId: item.orgId }}
                              className="break-all font-mono text-primary underline"
                            >
                              {item.orgId}
                            </Link>
                          </div>
                        </td>
                        <td className="min-w-56 max-w-lg p-4">
                          <p className="line-clamp-2 break-words">{item.brief.prompt}</p>
                          <Link
                            to="/human-editing-requests"
                            search={{ requestId: expanded ? undefined : item.requestId }}
                            aria-expanded={expanded}
                            aria-controls={`request-${item.requestId}`}
                            aria-label={`${expanded ? "Hide" : "View"} request from ${item.userName || item.email}`}
                            className="mt-2 inline-flex min-h-9 items-center gap-1.5 rounded-md border px-3 py-1.5 font-medium text-primary hover:bg-muted focus-visible:outline-2"
                          >
                            {expanded ? (
                              <ChevronDown className="size-4 shrink-0" aria-hidden="true" />
                            ) : (
                              <ChevronRight className="size-4 shrink-0" aria-hidden="true" />
                            )}
                            {expanded ? "Hide request" : "View request"}
                          </Link>
                        </td>
                        <td className="whitespace-nowrap p-4">
                          <Badge variant="outline" className={statuses[item.reviewStatus].color}>
                            {statuses[item.reviewStatus].label}
                          </Badge>
                        </td>
                        <td className="whitespace-nowrap p-4">
                          {["in_progress", "complete"].includes(item.reviewStatus)
                            ? editorName(item.assignedEditor) || "—"
                            : "—"}
                        </td>
                        <td className="whitespace-nowrap p-4 text-muted-foreground">
                          {item.approvedAtMs == null ? (
                            "—"
                          ) : (
                            <time
                              dateTime={new Date(item.approvedAtMs).toISOString()}
                              title={new Date(item.approvedAtMs).toISOString()}
                            >
                              <span className="block">
                                {new Date(item.approvedAtMs).toLocaleDateString()}
                              </span>
                              <span className="text-xs">
                                {new Date(item.approvedAtMs).toLocaleTimeString()}
                              </span>
                            </time>
                          )}
                        </td>
                        <td className="whitespace-nowrap p-4 text-muted-foreground">
                          <time
                            dateTime={new Date(item.submittedAtMs).toISOString()}
                            title={new Date(item.submittedAtMs).toLocaleString()}
                          >
                            {formatRelativeTime(item.submittedAtMs)}
                          </time>
                        </td>
                      </tr>
                      {expanded && (
                        <tr id={`request-${item.requestId}`}>
                          <td colSpan={6} className="border-b bg-muted/10 p-4">
                            <div className="max-w-[calc(100cqw-2rem)]">
                              <RequestDetail
                                requestId={item.requestId}
                                onSaved={() => void query.refetch()}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            {query.data.requests.length === 0 && (
              <div className="p-12 text-center text-muted-foreground">No requests yet.</div>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t p-3">
            <Button
              size="sm"
              variant="outline"
              disabled={cursors.length === 0}
              onClick={() => setCursors(cursors.slice(0, -1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!query.data.nextCursor}
              onClick={() => {
                if (query.data?.nextCursor) {
                  setCursors([...cursors, query.data.nextCursor]);
                }
              }}
            >
              Next
            </Button>
          </div>
        </Card>
      )}
      {requestId &&
        query.data &&
        !query.data.requests.some((item) => item.requestId === requestId) && (
          <RequestDetail
            key={requestId}
            requestId={requestId}
            onSaved={() => void query.refetch()}
          />
        )}
    </div>
  );
}

function RequestDetail({ requestId, onSaved }: { requestId: string; onSaved: () => void }) {
  const query = humanEditingRequestQuery.useQuery(
    { pathParams: { requestId } },
    {
      gcTime: 0,
      refetchOnMount: "always",
      refetchInterval: (query) =>
        query.state.data?.delivery?.status === "running" &&
        query.state.data.delivery.leaseUntilMs > Date.now()
          ? 15000
          : false,
    }
  );
  if (query.isPending) {
    return <p>Loading request…</p>;
  }
  if (!query.data) {
    return (
      <p role="alert" className="text-destructive">
        This request could not be loaded.
      </p>
    );
  }
  return (
    <RequestEditor
      key={requestId}
      item={query.data}
      onSaved={() => {
        void query.refetch();
        onSaved();
      }}
    />
  );
}

function RequestEditor({ item, onSaved }: { item: HumanEditingRequest; onSaved: () => void }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [download, setDownload] = useState<{ url: string; filename: string } | null>(null);
  const mounted = useRef(true);
  const downloadUrl = useRef<string | null>(null);
  useOnMountEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (downloadUrl.current) {
        URL.revokeObjectURL(downloadUrl.current);
        downloadUrl.current = null;
      }
    };
  });
  const editorPackage = humanEditingEditorPackageQuery.useQuery({
    pathParams: { requestId: item.requestId },
  });
  async function downloadPackage() {
    setIsDownloading(true);
    try {
      const result = await editorPackage.refetch();
      if (!result.data || result.isError) {
        throw new Error("Source media unavailable");
      }
      const prepared = await prepareEditorPackage(result.data);
      if (!mounted.current) {
        URL.revokeObjectURL(prepared.url);
        return;
      }
      if (downloadUrl.current) {
        URL.revokeObjectURL(downloadUrl.current);
      }
      downloadUrl.current = prepared.url;
      setDownload(prepared);
      toast.success("ZIP ready. Select Save ZIP to download it.");
    } catch {
      if (mounted.current) {
        toast.error(
          "Could not download the complete package. One or more source files may be unavailable. Please retry."
        );
      }
    } finally {
      if (mounted.current) {
        setIsDownloading(false);
      }
    }
  }
  return (
    <div className="grid items-start gap-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>Request</CardTitle>
            {download ? (
              <Button variant="outline" asChild>
                <a href={download.url} download={download.filename}>
                  <Download className="mr-2 size-4" />
                  Save ZIP
                </a>
              </Button>
            ) : (
              <Button
                variant="outline"
                disabled={isDownloading}
                onClick={() => void downloadPackage()}
              >
                <Download className="mr-2 size-4" />
                {isDownloading ? "Preparing ZIP…" : "Download editor package"}
              </Button>
            )}
          </div>
          <p className="break-all text-xs text-muted-foreground">
            Request ID: <span className="font-mono">{item.requestId}</span>
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <h3 className="text-sm font-medium">Prompt</h3>
            <p className="mt-2 whitespace-pre-wrap break-words rounded-md bg-muted/40 p-4 text-sm leading-relaxed">
              {item.brief.prompt}
            </p>
          </div>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-muted-foreground">Customer</dt>
              <dd className="mt-1 break-words">
                {item.userName}
                <br />
                {item.email}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Requested ratio</dt>
              <dd className="mt-1">{item.brief.format || "Not specified"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">User ID</dt>
              <dd className="mt-1 break-all font-mono text-xs">
                <Link
                  to="/users/view/$userId"
                  params={{ userId: item.userId }}
                  className="text-primary underline"
                >
                  {item.userId}
                </Link>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Organization ID</dt>
              <dd className="mt-1 break-all font-mono text-xs">
                <Link
                  to="/organizations/view/$orgId"
                  params={{ orgId: item.orgId }}
                  className="text-primary underline"
                >
                  {item.orgId}
                </Link>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Current balance</dt>
              <dd className="mt-1">
                {item.currentBalance == null
                  ? "Unavailable"
                  : `${item.currentBalance.toLocaleString()} credits`}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Price</dt>
              <dd className="mt-1">{item.cost.toLocaleString()} credits</dd>
            </div>
          </dl>
          {item.brief.duration && (
            <p className="text-sm">Requested length: {item.brief.duration}</p>
          )}
          <div>
            <Label>Source assets ({item.brief.assetIds.length})</Label>
            {editorPackage.isPending && (
              <p className="mt-2 text-sm text-muted-foreground">Loading media…</p>
            )}
            {editorPackage.isError && (
              <Button variant="outline" onClick={() => void editorPackage.refetch()}>
                Retry loading media
              </Button>
            )}
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {editorPackage.data?.files.map((file, index) => (
                <div
                  key={file.filename}
                  className="min-w-0 overflow-hidden rounded-md border bg-muted/20 p-2"
                >
                  {editorPackage.data && getEditorPackageFileLabel(editorPackage.data, index) && (
                    <p className="mb-2 text-sm font-medium">
                      @{getEditorPackageFileLabel(editorPackage.data, index)}
                    </p>
                  )}
                  <SourceMedia file={file} index={index} />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
      {item.reviewStatus === "awaiting_approval" ? (
        <ApprovalSection item={item} onApproved={onSaved} />
      ) : (
        <CompletionSection item={item} onSaved={onSaved} />
      )}
      <EmailDelivery item={item} onSaved={onSaved} />
    </div>
  );
}
